import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import type { CartLevelGiftRule, SelectedGift as CartLevelSelectedGift } from '../../../../types/front.graphql';
import {
    useAddCartLevelGiftsToCartMutation,
    useAvailableCartLevelGiftsQuery,
    useRemoveCartLevelGiftFromCartMutation,
} from '../../../../types';
import { useCart, cartItemReferenceStoreAPI } from '@kidslabs/module-quote';
import { useSelector } from '@legendapp/state/react';
import type { AvailableCartLevelGiftsViewInput } from './available-cart-level-gifts.types';
import { appConfig } from '@kidslabs/module-core';

export const useAvailableCartLevelGifts = ({ showSelectedOnly = false }: AvailableCartLevelGiftsViewInput) => {
    const { t } = useTranslation('flash-sale');

    // Get cartId from cart context
    const { cartId } = useCart();

    // Reactively check if any cart item reference is selected.
    // The query will be skipped when no items are selected.
    const hasSelectedItems = useSelector(() => {
        const references = cartItemReferenceStoreAPI.state.getItemsObservable().get() ?? [];
        return references.some(ref => ref?.isSelected === true);
    });

    const [addCartLevelGiftsToCartMutation, { loading: addGiftsToCartLoading, error: addGiftsToCartError }] =
        useAddCartLevelGiftsToCartMutation();

    const [removeCartLevelGiftFromCartMutation, { loading: removeGiftLoading }] =
        useRemoveCartLevelGiftFromCartMutation();

    // Use new API: availableCartLevelGifts
    // Only fetch when cart has selected items in cart item reference store
    const {
        data,
        loading: isAvailableCartLevelGiftsLoading,
        refetch: refetchAvailableCartLevelGifts,
    } = useAvailableCartLevelGiftsQuery({
        variables: {
            cartId: cartId ?? '',
        },
        fetchPolicy: 'cache-and-network',
        skip: !cartId || !hasSelectedItems,
        context: {
            uri: appConfig.magentoGraphQlUrl,
        },
    });

    // Use effective_rules from API (already filtered by priority/exclusivity on server)
    // These are the rules actually usable for gift selection
    const cartLevelGiftRules: CartLevelGiftRule[] = useMemo(() => {
        const effectiveRules = data?.availableCartLevelGifts?.effective_rules ?? [];
        return effectiveRules.filter(
            (item): item is NonNullable<typeof item> => item !== null
        ) as unknown as CartLevelGiftRule[];
    }, [data]);

    // All rules (including ineligible/blocked) for progress bar or full UI display
    const allCartLevelGiftRules: CartLevelGiftRule[] = useMemo(() => {
        const items = data?.availableCartLevelGifts?.items ?? [];
        return items.filter(
            (item): item is NonNullable<typeof item> => item !== null
        ) as unknown as CartLevelGiftRule[];
    }, [data]);

    // Active rule ID (rule with selections or highest priority exclusive rule)
    const activeRuleId = useMemo(() => {
        return data?.availableCartLevelGifts?.active_rule_id ?? null;
    }, [data]);

    // All rule IDs that currently have gift selections applied
    const activeRuleIds: number[] = useMemo(() => {
        return data?.availableCartLevelGifts?.active_rule_ids ?? [];
    }, [data]);

    // Whether an exclusive rule is blocking other rules
    const hasExclusiveConflict = useMemo(() => {
        return data?.availableCartLevelGifts?.has_exclusive_conflict ?? false;
    }, [data]);

    const hasGiftsContent = useMemo(() => {
        return cartLevelGiftRules.length > 0;
    }, [cartLevelGiftRules]);

    // Get all selected gifts from effective rules
    const selectedGiftsFromServer: CartLevelSelectedGift[] = useMemo(() => {
        return cartLevelGiftRules.flatMap(rule =>
            (rule.selected_gifts ?? []).filter((gift): gift is NonNullable<typeof gift> => gift !== null)
        );
    }, [cartLevelGiftRules]);

    // Calculate total selected gifts count
    const selectedGiftsCount = useMemo(() => {
        return selectedGiftsFromServer.reduce((total, gift) => total + gift.quantity, 0);
    }, [selectedGiftsFromServer]);

    const title = useMemo(() => {
        if (showSelectedOnly) {
            return t('text_view_all_selected_gift_count', 'Bạn có {{count}} quà tặng! Xem ngay', {
                count: selectedGiftsCount,
            });
        }

        return selectedGiftsCount > 0
            ? t('text_selected_gift_count', { count: selectedGiftsCount })
            : t('text_select_gift_by_cart_price');
    }, [t, selectedGiftsCount, showSelectedOnly]);

    const iconProps = useMemo(() => {
        return {
            src: 'https://static.kidsplaza.vn/assets/icons/active-gift.png',
            alt: 'Quà tặng của bạn',
        };
    }, []);

    const giftsListLoading = useMemo(() => {
        return isAvailableCartLevelGiftsLoading || addGiftsToCartLoading || removeGiftLoading;
    }, [addGiftsToCartLoading, isAvailableCartLevelGiftsLoading, removeGiftLoading]);

    return {
        selectedGiftsCount,
        cartLevelGiftRules,
        allCartLevelGiftRules,
        activeRuleId,
        activeRuleIds,
        hasExclusiveConflict,
        hasGiftsContent,
        iconProps,
        title,
        giftsListLoading,
        refetchAvailableCartLevelGifts,
        removeCartLevelGiftFromCartMutation,
        addGiftsToCartError,
        addCartLevelGiftsToCartMutation,
    };
};
